import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generatePath } from 'react-router-dom';
import { Response, Pagination, Filters } from '@ui/src';
import { CountryItem } from '../../types';
import { countriesApi } from '../services/countries';

import ROUTES from '../../../../routes';

export interface CountriesListState {
  requestInProgress: boolean;
  values?: CountryItem[];
  country?: CountryItem;
  sort?: {
    sortProp: string;
    sortDir: string;
  };
  filters?: Filters;
  pagination?: Pagination;
  selectedCountries?: CountryItem[];
  selectedCountryIds?: string[];
  activeCount?: number;
  disableActivateBtn: boolean;
  disableInactivateBtn: boolean;
}

const initialState: CountriesListState = {
  values: undefined,
  sort: undefined,
  requestInProgress: true,
  filters: undefined,
  disableActivateBtn: true,
  disableInactivateBtn: true,
};

export const countriesListSlice = createSlice({
  name: 'countriesList',
  initialState,
  reducers: {
    onSortList: (
      state,
      action: PayloadAction<{ name: string; direction: 'asc' | 'desc' }>,
    ) => {
      state.sort = {
        sortDir: action.payload.direction,
        sortProp: action.payload.name,
      };
      state.requestInProgress = true;
    },
    onSortListSuccess: (state, action: PayloadAction<CountryItem[]>) => {
      state.requestInProgress = false;
      state.values = action.payload;
    },
    onFilterList: (state, action: PayloadAction<Filters>) => {
      state.requestInProgress = true;
      state.filters = action.payload;
    },
    onFilterListSuccess: (state, action: PayloadAction<CountryItem[]>) => {
      state.requestInProgress = false;
      state.values = action.payload;
    },
    onSelectedRows: (state, action: PayloadAction<string[]>) => {
      state.selectedCountryIds = action.payload;

      const selectedCountries =
        state.values?.filter((item) => action.payload.includes(item.id)) || [];

      state.selectedCountries = selectedCountries;

      if (selectedCountries.length > 0) {
        const allActivated = selectedCountries.every((item) => item.active);
        const allDisabled = selectedCountries.every((item) => !item.active);

        if (allActivated) {
          state.disableInactivateBtn = false;
        } else if (allDisabled) {
          state.disableActivateBtn = false;
        } else {
          state.disableActivateBtn = false;
          state.disableInactivateBtn = false;
        }
      } else {
        state.disableActivateBtn = true;
        state.disableInactivateBtn = true;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        countriesApi.endpoints.getEmployeesCountries.matchFulfilled,
        (state, action: PayloadAction<Response<CountryItem>>) => {
          state.values = action.payload.data.edges.map((item) => ({
            ...item.node,
            url: generatePath(ROUTES.COUNTRY, { id: item.node.id }),
          }));

          state.pagination = action.payload.data.pageInfo;
        },
      )
      .addMatcher(
        countriesApi.endpoints.getCountry.matchFulfilled,
        (state, action: PayloadAction<{ data: CountryItem }>) => {
          state.country = action.payload.data;
        },
      )
      .addMatcher(
        countriesApi.endpoints.updateCountryRiskScore.matchFulfilled,
        (state, action: PayloadAction<{ data: CountryItem }>) => {
          state.values = state.values?.map((item) => {
            const result = { ...item };

            if (result.id === action.payload.data.id) {
              result.riskScore = action.payload.data.riskScore;
            }

            return result;
          });

          state.country = action.payload.data;
        },
      )
      .addMatcher(
        countriesApi.endpoints.updateCountryActivity.matchFulfilled,
        (state, action: PayloadAction<{ data: CountryItem }>) => {
          state.values = state.values?.map((item) => {
            const result = { ...item };

            if (result.id === action.payload.data.id) {
              result.active = action.payload.data.active;
            }

            return result;
          });

          state.country = action.payload.data;
        },
      )
      .addMatcher(
        countriesApi.endpoints.getCountActiveCountries.matchFulfilled,
        (state, action: PayloadAction<Response<CountryItem>>) => {
          state.activeCount = action.payload.data.pageInfo.totalCount;
        },
      );
  },
});

export const {
  onSortList,
  onSortListSuccess,
  onFilterList,
  onFilterListSuccess,
  onSelectedRows,
} = countriesListSlice.actions;

export default countriesListSlice.reducer;
